import { useEffect, useState } from 'react';
import { X, Share } from 'lucide-react';
import './InstallPrompt.css';

// `beforeinstallprompt` isn't in the standard lib.dom types.
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

const DISMISS_KEY = 'pb_installPromptDismissed';
// Only nudge on mobile portrait — desktop has its own install UX in the address bar.
const MOBILE_QUERY = '(orientation: portrait) and (pointer: coarse)';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayStandalone || iosStandalone;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}

/**
 * Add-to-Home-Screen nudge (RegalPortal PWA-lite pattern). Android/Chromium gets the captured
 * native install prompt; iOS Safari gets manual steps (no programmatic install exists there).
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosSafari] = useState<boolean>(() => isIos());
  const [standalone] = useState<boolean>(() => isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobilePortrait, setMobilePortrait] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Android/Chromium: capture the install event so we can trigger it from our own button.
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setDismissed(true);
      try {
        localStorage.setItem(DISMISS_KEY, '1');
      } catch {
        /* ignore */
      }
    };
    const mql = window.matchMedia(MOBILE_QUERY);
    const onMedia = () => setMobilePortrait(mql.matches);

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    mql.addEventListener('change', onMedia);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      mql.removeEventListener('change', onMedia);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    setShowIosHelp(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const handleAction = () => {
    if (deferredPrompt) {
      const promptEvent = deferredPrompt;
      promptEvent.prompt().catch(() => setDeferredPrompt(null));
      promptEvent.userChoice.then(() => setDeferredPrompt(null)).catch(() => setDeferredPrompt(null));
    } else {
      setShowIosHelp(true);
    }
  };

  const installable = !!deferredPrompt || iosSafari;
  if (standalone || dismissed || !mobilePortrait || !installable) return null;

  return (
    <>
      <div className="install-banner" role="region" aria-label="Add to Home Screen">
        {/* The crest-derived render the home screen will actually get (neutral until a logo exists). */}
        <img className="install-banner-icon" src="/api/public/icons/192.png" alt="" aria-hidden="true" />
        <div className="install-banner-text">
          <strong>Add to Home Screen</strong>
          <span>Opens full-screen, with no browser bars</span>
        </div>
        <button type="button" className="install-banner-btn" onClick={handleAction}>
          {deferredPrompt ? 'Install' : 'How'}
        </button>
        <button type="button" className="install-banner-close" onClick={dismiss} aria-label="Dismiss">
          <X size={18} />
        </button>
      </div>

      {showIosHelp && (
        <div className="install-help-backdrop" onClick={() => setShowIosHelp(false)}>
          <div
            className="install-help"
            role="dialog"
            aria-label="How to add to Home Screen"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="install-help-close"
              onClick={() => setShowIosHelp(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <h3>Add to Home Screen</h3>
            <ol>
              <li>
                Tap the <strong>Share</strong> icon{' '}
                <Share size={15} className="install-help-share" /> in Safari&apos;s toolbar
              </li>
              <li>
                Scroll down and tap <strong>Add to Home Screen</strong>
              </li>
              <li>
                Tap <strong>Add</strong>, then open it from your Home Screen
              </li>
            </ol>
            <p className="install-help-note">It opens full-screen — no address bar or tabs.</p>
          </div>
        </div>
      )}
    </>
  );
}
